import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { apiClient } from '@/core/http/client';
import type { ApiError } from '@/models/apiError';
import { auftraegeKeys } from '@/features/auftraege/api/auftraege.keys';

/** Input for `DELETE /api/v1/auftraege/{id}` — just the path id, no request body. */
export interface AuftragEntwurfLoeschenInput {
  readonly id: string;
}

async function deleteAuftragEntwurf(input: AuftragEntwurfLoeschenInput): Promise<void> {
  // Non-2xx responses (404 — not visible/does not exist; 409 AUFTRAG_NICHT_LOESCHBAR when the
  // order already left status=DRAFT, BR-035) become a typed ApiError before this line is
  // reached — see client.ts. A successful delete returns 204 with no body.
  await apiClient.DELETE('/api/v1/auftraege/{id}', {
    params: { path: { id: input.id } },
  });
}

/**
 * Deletes an order draft while it is still in `status=DRAFT` (US-E02-03 step 1 "Entwurf
 * löschen", `e02-03-ak-9`, BR-035). Once submitted, an order can no longer be deleted this way
 * and the request fails with a `409` `ApiError` (`code=AUFTRAG_NICHT_LOESCHBAR`). Removes the
 * draft's now-stale detail query (`useProthetikAuftragEntwurf`) from the cache on success,
 * rather than invalidating it, since a refetch would only ever 404. Also invalidates the
 * order-list query (`auftraegeKeys.all`) so the Auftragsübersicht stops showing the deleted
 * draft immediately, regardless of which caller triggered the delete.
 */
export function useAuftragEntwurfLoeschen(): UseMutationResult<
  void,
  ApiError,
  AuftragEntwurfLoeschenInput
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAuftragEntwurf,
    onSuccess: (_data, variables) => {
      queryClient.removeQueries({ queryKey: auftraegeKeys.detail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: auftraegeKeys.all });
    },
  });
}
